<template>
    <div class="RiskProfileAssignment" :class="{'RiskProfileAssignment--error': shouldShowError}">
        <RiskProfileAssignmentHeader
            :should-show-error="shouldShowError"
            :error-msg="errorMsg"
        />
        <RbacDisableGuard
            v-slot="{ isDisabledByRbac }"
            :features="[RbacFeature.decisioningManageAssignmentRules]"
        >
            <div class="RiskProfileAssignment__body">
                <RiskProfileAssignmentOptions
                    :assignment-option="assignmentRule ? assignmentRule.type : null"
                    :is-disabled="isDisabledByRbac"
                    @set-assignment-rule-option="onOptionChange"
                />
                <RiskProfileAssignmentContent
                    :requirement-types="requirementTypes"
                    :custom-properties="customProperties"
                    :assignment-rule="assignmentRule"
                    :is-loading-custom-properties="isLoadingCustomProperties"
                    :is-collateral-profile="isCollateralProfile"
                    :get-requirement-type-label="getRequirementTypeLabel"
                    :is-disabled="isDisabledByRbac"
                    @update-rules="$emit('update-assignment-rule', $event)"
                />
            </div>
        </RbacDisableGuard>
    </div>
</template>

<style lang="scss">
.RiskProfileAssignment {
    display: flex;
    flex-direction: column;
    min-height: 200px;
    min-width: 316px;
    background-color: #fff;
    border: 1px solid var(--eidTechGray-25);
    border-radius: 8px;

    &--error {
        border-color: var(--eidDangerRed);
    }

    &__body {
        flex: 1;
        padding: 0 15px;
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .RiskProfileAssignmentHeader {
        padding: 0 15px;
        margin-bottom: 16px;
    }

    .RiskProfileAssignmentContent {
        flex: 1;
        min-height: 0;
    }

    .EntitySearchSelection {
        height: 50%;
        min-height: 450px;
    }

    &__addButton {
        margin: 10px;
    }
}
</style>

<script lang="ts">
    import { PropType } from 'vue';

    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import RbacDisableGuard from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableGuard.vue';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { AssignmentRuleType, AssignmentRule } from '@evidentid/tprm-portal-lib/models/decisioning/AssignmentRule.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import { GetRequirementTypeLabel } from '@/modules/decisioning-criteria/components/CreateEditRiskProfile/types';
    import { BasicAssignmentRule } from '@/modules/decisioning-criteria/types';
    import { convertToBasicAssignmentRules } from '@/modules/decisioning-criteria/utils/parseRulesLogic';

    import RiskProfileAssignmentContent from './sub-components/RiskProfileAssignmentContent.vue';
    import RiskProfileAssignmentHeader from './sub-components/RiskProfileAssignmentHeader.vue';
    import RiskProfileAssignmentOptions from './sub-components/RiskProfileAssignmentOptions.vue';

    @Component({
        emits: [ 'update-assignment-rule' ],
        components: {
            RiskProfileAssignmentHeader,
            RiskProfileAssignmentContent,
            RiskProfileAssignmentOptions,
            RbacDisableGuard,
        },
    })
    export default class RiskProfileAssignment extends Vue {
        @Prop({ type: Array as PropType<TprmRequirementType[]>, default: (): TprmRequirementType[] => [] })
        private requirementTypes!: TprmRequirementType[];

        @Prop({ type: Object, default: null })
        private assignmentRule!: AssignmentRule | null;

        @Prop({ type: Boolean, default: false })
        private showError!: boolean;

        @Prop({ type: Array, default: () => [] })
        private customProperties!: CustomProperty[];

        @Prop({ type: Function as PropType<GetRequirementTypeLabel>, required: true })
        private getRequirementTypeLabel!: GetRequirementTypeLabel;

        @Prop({ type: Boolean, default: false })
        private isLoadingCustomProperties!: boolean;

        @Prop({ type: Boolean, default: false })
        private isCollateralProfile!: boolean;

        private RbacFeature = RbacFeature;

        private get selectedTypeRules(): BasicAssignmentRule[][] {
            if (!this.assignmentRule) {
                return [];
            }
            // Extract expression from RiskProfileAssignmentRule union type
            if (this.assignmentRule.type === AssignmentRuleType.selected &&
                this.assignmentRule.expression
            ) {
                return convertToBasicAssignmentRules(this.assignmentRule.expression);
            }
            return [];
        }

        private get errorMsg(): string | null {
            const emptyMsg = 'At least one assignment rule must be configured';
            const hasEmptyViolation = this.assignmentRule === null ||
                this.assignmentRule.type === AssignmentRuleType.selected && this.selectedTypeRules.length === 0 ||
                this.assignmentRule.type === AssignmentRuleType.single && !this.assignmentRule.entityId;
            return hasEmptyViolation ? emptyMsg : null;
        }

        private get shouldShowError(): boolean {
            return this.showError && Boolean(this.errorMsg);
        }

        private onOptionChange(option: AssignmentRuleType): void {
            let defaultAssignmentRule: AssignmentRule | null = this.assignmentRule;
            if (option === AssignmentRuleType.selected) {
                defaultAssignmentRule = { type: AssignmentRuleType.selected, expression: null };
            } else if (option === AssignmentRuleType.all) {
                defaultAssignmentRule = { type: AssignmentRuleType.all };
            } else if (option === AssignmentRuleType.single) {
                defaultAssignmentRule = { type: AssignmentRuleType.single, entityId: '' };
            }
            this.$emit('update-assignment-rule', defaultAssignmentRule);
        }
    }
</script>
